import { useApi } from '~/api';
import { components, operations } from '~~/api-types';
import { ContentSocialType } from '~/api/contents';

// ============ Enums ============

export enum LearningStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  PAUSED = 'paused',
  ARCHIVED = 'archived',
}

export enum ActivityType {
  VIDEO = 'video',
  AUDIO = 'audio',
  FILE_DOWNLOAD = 'file-download',
  RESOURCE = 'resource',
  SOCIAL_POST = 'social-post',
  SCORM = 'scorm',
  ASSESSMENT = 'assessment',
  ARTICLE = 'article',
}

export enum UserProgressStatus {
  NOT_STARTED = 'not-started',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  PASSED = 'passed',
  NOT_PASSED = 'not-passed',
  AWAITING_REVIEW = 'awaiting-review',
}

export enum AssessmentAttemptStatus {
  IN_PROGRESS = 'in-progress',
  PASSED = 'passed',
  NOT_PASSED = 'not-passed',
}

export enum AssessmentQuestionType {
  MULTIPLE_ANSWER = 'multiple-answer',
  SINGLE_ANSWER = 'single-answer',
  SHORT_ANSWER = 'short-answer',
  VIDEO_ANSWER = 'video-answer',
}

export enum AssessmentMediaType {
  TEXT = 'text',
  PDF = 'pdf',
  IMAGE = 'image',
  VIDEO = 'video',
}

// ============ Type Definitions ============

// Curriculum types
export type CurriculumDto = components['schemas']['CurriculumResponseDto'];
export type PaginatedCurriculumsDto =
  components['schemas']['PaginatedCurriculumsResponseDto'];
export type CurriculumModuleDto =
  components['schemas']['ModuleInCurriculumResponseDto'];

// Module types
export type ModuleDto = components['schemas']['ModuleResponseDto'];
export type ModuleActivityDto =
  components['schemas']['ModuleActivityWithProgressDto'];

// Module activities response (includes module info + activities)
export type ModuleInfoDto = {
  id: number;
  slug: string;
  title: string;
  description?: string | null;
  headerImageUrl?: string | null;
  forceSequentialLearning: boolean;
  isSequentialBypassed?: boolean;
  holdOnFailedOrInReview: boolean;
  congratsPopupHeadline?: string | null;
  congratsPopupBody?: string | null;
  congratsPopupCtaText?: string | null;
  congratsPopupCtaUrl?: string | null;
  backgroundColor: string;
  onBackgroundColor: string;
  onBackgroundVariantColor: string;
  surfaceColor: string;
  onSurfaceColor: string;
  onSurfaceVariantColor: string;
  primaryColor: string;
  onPrimaryColor: string;
  secondaryColor: string;
  onSecondaryColor: string;
  tertiaryColor: string;
  onTertiaryColor: string;
  lineColor: string;
  successColor: string;
  infoColor: string;
  warningColor: string;
  errorColor: string;
  helpBoxColor: string;
  onHelpBoxColor: string;
  disabledColor: string;
  earnedRewardBudgetAmountInCents?: number | null;
};

export type ModuleActivitiesResponseDto = {
  module: ModuleInfoDto;
  activities: ModuleActivityDto[];
};

// User Module types (for direct module enrollment)
export type UserModuleDto = {
  id: number;
  title: string;
  slug: string;
  description?: string | null;
  logoUrl?: string | null;
  headerImageUrl?: string | null;
  status: LearningStatus;
  progressPercent: number;
  userStatus: string;
  activitiesCount?: number;
  completedActivitiesCount?: number;
  canRetake?: boolean;
  isLocked?: boolean;
  sequenceOrder?: number;
  sequenceTotal?: number;
};

export type PaginatedUserModulesDto = {
  nodes: UserModuleDto[];
  totalCount: number;
};

export type GetMyModulesQueryParams = {
  page?: number;
  perPage?: number;
};

// Activity types
export type ActivityDto = components['schemas']['ActivityResponseDto'];
type SocialPostData = {
  contentSocialId: number;
  type: string;
  description: string;
  fileUrl?: string;
  isEditable: boolean;
  captionEditing: components['schemas']['SocialCaptionEditing'];
  isCustomizable: boolean;
  showDownloadButton: boolean;
  isAvailabilityDateEnabled: boolean;
  availabilityStartDate?: string;
  socialFiles: {
    id: number;
    caption?: string;
    fileId: number;
    fileUrl: string;
    position: number;
  }[];
  quadrants: {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    page?: number;
  }[];
};

export type LaunchActivityResponseDto =
  components['schemas']['LaunchActivityResponseDto'] & {
    scormCourse?: {
      previewImageUrl: string;
      description: string;
      scormLaunchUrl: string;
    };
    resource?: {
      fileUrl?: string;
      description: string;
      displayCtaButton: boolean;
      ctaButtonUrl?: string;
      ctaButtonText?: string;
    };
    socialPost?: SocialPostData;
    socialPosts?: SocialPostData[];
    disableFastForwarding?: boolean;
    audioDurationSeconds?: number;
    audioCoverImageUrl?: string;
    // Activity-level theme overrides. Null = no override, fall back to module/org theme.
    backgroundColor?: string | null;
    onBackgroundColor?: string | null;
    onBackgroundVariantColor?: string | null;
    surfaceColor?: string | null;
    onSurfaceColor?: string | null;
    onSurfaceVariantColor?: string | null;
    primaryColor?: string | null;
    onPrimaryColor?: string | null;
    secondaryColor?: string | null;
    onSecondaryColor?: string | null;
    tertiaryColor?: string | null;
    onTertiaryColor?: string | null;
    lineColor?: string | null;
    successColor?: string | null;
    infoColor?: string | null;
    warningColor?: string | null;
    errorColor?: string | null;
    disabledColor?: string | null;
  };
export type ActivityProgressDto =
  components['schemas']['ActivityProgressResponseDto'];
export type ActivityVideoDto =
  components['schemas']['ActivityVideoResponseDto'];

// Assessment types

export type AssessmentQuestionDto = Omit<
  components['schemas']['AssessmentQuestionUserViewDto'],
  'questionType'
> & {
  questionType:
    | 'multiple-answer'
    | 'single-answer'
    | 'short-answer'
    | 'video-answer';
  maxCharacterLimit: number | null;
};

export type AssessmentDto = Omit<
  components['schemas']['AssessmentUserViewDto'],
  'questions' | 'maxRetakes'
> & {
  questions: AssessmentQuestionDto[];
  maxRetakes: number | null;
};

export type AssessmentAnswerDto =
  components['schemas']['AssessmentAnswerUserViewDto'];

export type AssessmentQuestionResultDto = Omit<
  components['schemas']['AssessmentQuestionResultDto'],
  'isCorrect'
> & {
  isCorrect: boolean | null;
  textResponse?: string | null;
  feedback?: string | null;
};

export type AssessmentResultDto = Omit<
  components['schemas']['AssessmentResultResponseDto'],
  'score' | 'passed' | 'questionResults'
> & {
  score: number | null;
  passed: boolean | null;
  status: 'in-progress' | 'passed' | 'not-passed' | 'awaiting-review';
  questionResults?: AssessmentQuestionResultDto[];
};

type UserAssessmentAnswerSnapshot = {
  id: number;
  questionId: number;
  selectedAnswerId: number | null;
  isCorrect: boolean | null;
  textResponse: string | null;
  submittedAt: string | null;
};

export type AssessmentAttemptDto = Omit<
  components['schemas']['UserAssessmentAttemptResponseDto'],
  'answers' | 'questionOrder'
> & {
  answers?: UserAssessmentAnswerSnapshot[];
  questionOrder?: number[] | null;
};

// Query param types
export type GetMyCurriculumsQueryParams =
  operations['AppGetMyCurriculumsEndpoint_handler']['parameters']['query'];

// ============ API Functions ============

/**
 * Get list of curriculums enrolled for current user
 */
export const apiGetMyCurriculums = (
  params?: GetMyCurriculumsQueryParams,
): Promise<PaginatedCurriculumsDto> => {
  const api = useApi();
  return api('/app/learning/my-curriculums', { params });
};

/**
 * Get list of modules assigned to current user (direct enrollment or via curriculum)
 * Phase 1: Used as primary learning access point
 */
export const apiGetMyModules = async (
  params?: GetMyModulesQueryParams,
): Promise<PaginatedUserModulesDto> => {
  const api = useApi();
  const result = await api('/app/learning/my-modules', { params });
  return result;
};

/**
 * Get single curriculum details (requires enrollment)
 */
export const apiGetCurriculumDetails = (
  curriculumId: number,
): Promise<CurriculumDto> => {
  const api = useApi();
  return api(`/app/learning/curriculums/${curriculumId}`);
};

/**
 * Get modules within a curriculum with progress
 */
export const apiGetCurriculumModules = (
  curriculumId: number,
): Promise<CurriculumModuleDto[]> => {
  const api = useApi();
  return api(`/app/learning/curriculums/${curriculumId}/modules`);
};

/**
 * Get activities within a module with progress
 * @param curriculumId - Optional for Phase 1 direct module enrollment
 */
export const apiGetModuleActivities = (
  moduleId: number,
  curriculumId?: number,
): Promise<ModuleActivitiesResponseDto> => {
  const api = useApi();
  return api<ModuleActivitiesResponseDto>(
    `/app/learning/modules/${moduleId}/activities`,
    { params: curriculumId ? { curriculumId } : undefined },
  );
};

/**
 * Get activities within a module by slug (or numeric ID fallback)
 */
export const apiGetModuleActivitiesBySlug = (
  moduleSlug: string,
  curriculumId?: number,
): Promise<ModuleActivitiesResponseDto> => {
  const api = useApi();
  return api<ModuleActivitiesResponseDto>(
    `/app/learning/modules/${moduleSlug}/activities`,
    { params: curriculumId ? { curriculumId } : undefined },
  );
};

/**
 * Launch an activity and get content details
 * @param curriculumId - Optional for Phase 1 direct module enrollment
 */
export const apiLaunchActivity = (
  activityId: number,
  moduleId: number,
  curriculumId?: number,
): Promise<LaunchActivityResponseDto> => {
  const api = useApi();
  const params: Record<string, number> = { moduleId };
  if (curriculumId) {
    params.curriculumId = curriculumId;
  }
  return api(`/app/learning/activities/${activityId}/launch`, { params });
};

/**
 * Update activity progress
 * @param data.curriculumId - Optional for Phase 1 direct module enrollment
 */
export const apiUpdateActivityProgress = (
  activityId: number,
  data: {
    curriculumId?: number;
    moduleId: number;
    progressPercent?: number;
    videoWatchedSeconds?: number;
    completed?: boolean;
    failed?: boolean;
    score?: number;
  },
): Promise<ActivityProgressDto> => {
  const api = useApi();
  return api(`/app/learning/activities/${activityId}/progress`, {
    method: 'PATCH',
    body: data,
  });
};

/**
 * Get assessment details (without correct answers)
 */
export const apiGetAssessment = (
  assessmentId: number,
  moduleId?: number,
): Promise<AssessmentDto> => {
  const api = useApi();
  return api(`/app/learning/assessments/${assessmentId}`, {
    params: moduleId ? { moduleId } : undefined,
  });
};

/**
 * Get the user's last completed assessment attempt result
 */
export const apiGetAssessmentLastResult = (
  assessmentId: number,
  moduleId?: number,
): Promise<AssessmentResultDto | null> => {
  const api = useApi();
  return api(`/app/learning/assessments/${assessmentId}/my-last-result`, {
    params: moduleId ? { moduleId } : undefined,
  });
};

/**
 * Start a new assessment attempt
 */
export const apiStartAssessmentAttempt = (
  assessmentId: number,
  moduleId?: number,
  curriculumId?: number,
): Promise<AssessmentAttemptDto> => {
  const api = useApi();
  return api(`/app/learning/assessments/${assessmentId}/attempts`, {
    method: 'POST',
    body: {
      ...(moduleId ? { moduleId } : {}),
      ...(curriculumId ? { curriculumId } : {}),
    },
  });
};

export const apiCompleteAssessmentAttempt = (
  attemptId: number,
  answers: Array<{
    questionId: number;
    answerId?: number;
    textResponse?: string;
    videoFileId?: number;
    durationSeconds?: number;
  }>,
): Promise<AssessmentResultDto> => {
  const api = useApi();
  return api(`/app/learning/assessments/attempts/${attemptId}/complete`, {
    method: 'POST',
    body: { answers },
  });
};

export const apiUpsertShortAnswerDraft = (
  attemptId: number,
  questionId: number,
  textResponse: string,
): Promise<{ savedAt: string }> => {
  const api = useApi();
  return api(
    `/app/learning/assessments/attempts/${attemptId}/answers/${questionId}/draft`,
    {
      method: 'PATCH',
      body: { textResponse },
    },
  );
};

/**
 * Create social share URL for a learning activity
 */
export const apiCreateLearningSocialShareUrl = (
  activityId: number,
  data: {
    moduleId: number;
    curriculumId?: number;
    contentSocialId?: number;
    draftId?: number;
    description?: string;
    socialType?: ContentSocialType;
    socialFiles?: Array<{ fileId: number; caption: string | null }>;
    postPlannedAt?: string | null;
  },
): Promise<{ shareUrl: string }> => {
  const api = useApi();
  return api(`/app/learning/activities/${activityId}/social-share-url`, {
    method: 'POST',
    body: data,
  });
};

/**
 * Activity metric types (mirrors backend ActivityMetricType)
 */
export enum ActivityMetricType {
  VIEW = 'view',
  ACTION_BUTTON = 'action-button',
  FILE_DOWNLOAD = 'file-download',
  FACEBOOK_POST_DOWNLOAD = 'facebook-post-download',
  INSTAGRAM_POST_DOWNLOAD = 'instagram-post-download',
  TWITTER_POST_DOWNLOAD = 'twitter-post-download',
  LINKEDIN_POST_DOWNLOAD = 'linkedin-post-download',
}

/**
 * Create an activity metric
 */
export const apiCreateActivityMetric = (
  activityId: number,
  data: {
    metricType: ActivityMetricType;
    moduleId: number;
  },
): Promise<void> => {
  const api = useApi();
  return api(`/app/learning/activities/${activityId}/metrics`, {
    method: 'POST',
    body: data,
  });
};

// ============ Helper Functions ============

/**
 * Get label for activity type
 */
export const useActivityTypeLabelMap = () =>
  computed<Record<ActivityType, string>>(() => {
    const { t } = useI18n();
    return {
      [ActivityType.VIDEO]: t('learning.activityType.video'),
      [ActivityType.AUDIO]: t('learning.activityType.audio'),
      [ActivityType.FILE_DOWNLOAD]: t('learning.activityType.fileDownload'),
      [ActivityType.RESOURCE]: t('learning.activityType.resource'),
      [ActivityType.SOCIAL_POST]: t('learning.activityType.socialPost'),
      [ActivityType.SCORM]: t('learning.activityType.scorm'),
      [ActivityType.ASSESSMENT]: t('learning.activityType.assessment'),
      [ActivityType.ARTICLE]: t('learning.activityType.article'),
    };
  });

/**
 * Get label for progress status
 */
export const useProgressStatusLabelMap = () =>
  computed<Record<UserProgressStatus, string>>(() => {
    const { t } = useI18n();
    return {
      [UserProgressStatus.NOT_STARTED]: t('learning.status.notStarted'),
      [UserProgressStatus.IN_PROGRESS]: t('learning.status.inProgress'),
      [UserProgressStatus.COMPLETED]: t('learning.status.completed'),
      [UserProgressStatus.PASSED]: t('learning.status.passed'),
      [UserProgressStatus.NOT_PASSED]: t('learning.status.notPassed'),
      [UserProgressStatus.AWAITING_REVIEW]: t('learning.status.inReview'),
    };
  });

/**
 * Get color for progress status
 */
export const getProgressStatusColor = (status: UserProgressStatus): string => {
  switch (status) {
    case UserProgressStatus.NOT_STARTED: {
      return 'gray';
    }
    case UserProgressStatus.IN_PROGRESS:
    case UserProgressStatus.AWAITING_REVIEW: {
      return 'blue';
    }
    case UserProgressStatus.COMPLETED:
    case UserProgressStatus.PASSED: {
      return 'green';
    }
    case UserProgressStatus.NOT_PASSED: {
      return 'red';
    }
    default: {
      return 'gray';
    }
  }
};
